import {
  AI_AUTOPILOT_USER_ID,
  CHATBOT_USER_ID,
} from "@redotech/ai-autopilot-constants/constant";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { GetUser as User } from "@redotech/redo-model/user";
import {
  RedoAvatar,
  type AvatarContent,
} from "@redotech/redo-web/arbiter-components/avatar/redo-avatar";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import Stars01Icon from "@redotech/redo-web/icon-old/stars-01.svg";
import { memo, useMemo } from "react";

interface EditableAssigneeCellProps {
  conversation: SupportTicketListItemResponse;
}

function isAiUser(user: User): boolean {
  return user._id === CHATBOT_USER_ID || user._id === AI_AUTOPILOT_USER_ID;
}

function getAvatarContent(user: User): AvatarContent {
  if (isAiUser(user)) {
    return { type: "icon", Icon: Stars01Icon };
  }
  const imageUrl = getUserAvatarUrl({ email: user.email, userId: user._id });
  return {
    type: "image",
    imageUrl,
    altText: user.name,
    backup: { type: "text", text: user.name },
  };
}

export const EditableAssigneeCell = memo(function EditableAssigneeCell({
  conversation,
}: EditableAssigneeCellProps) {
  const team = useRequiredContext(TeamContext);

  const assignee = useMemo(() => {
    const { mongoAssigneeId } = conversation;
    if (!mongoAssigneeId) {
      return undefined;
    }
    const hexId = MongoUserId.toObjectId(mongoAssigneeId).toHexString();
    const teamUser = team.users.find(
      (u) => typeof u.user === "object" && u.user._id === hexId,
    );
    //@Connor fix this
    return teamUser && typeof teamUser.user === "object"
      ? (teamUser.user as User)
      : undefined;
  }, [conversation, team.users]);

  if (!assignee) {
    return null;
  }

  if (isAiUser(assignee)) {
    return (
      <Flex align="center">
        <RedoIcon color="orange" Icon={Stars01Icon} size={20} />
      </Flex>
    );
  }

  return (
    <Flex align="center">
      <RedoAvatar content={getAvatarContent(assignee)} size="xs" />
    </Flex>
  );
});
